import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
  List,
  ListItem,
  ListItemText,
  Box,
  Chip,
  useMediaQuery,
} from "@material-ui/core";
import { useTheme } from "@material-ui/core/styles";
import { IonAlert } from "@ionic/react";
import AsignacionesV2Service from "../services/asignacionesV2";

interface Props {
  open: boolean;
  psId: string | number;
  corte: any | null;
  operario: { operario?: string; legajo?: string | number } | null;
  onClose: () => void;
  onReemplazado: () => void;
}

// Mapeo dia -> campos del corte que devuelve el backend (lun_ent..dom_sal).
const DIAS = [
  { label: "Lun", ent: "lun_ent", sal: "lun_sal" },
  { label: "Mar", ent: "mar_ent", sal: "mar_sal" },
  { label: "Mié", ent: "mie_ent", sal: "mie_sal" },
  { label: "Jue", ent: "jue_ent", sal: "jue_sal" },
  { label: "Vie", ent: "vie_ent", sal: "vie_sal" },
  { label: "Sáb", ent: "sab_ent", sal: "sab_sal" },
  { label: "Dom", ent: "dom_ent", sal: "dom_sal" },
];

// Resumen legible del horario del corte: ["Lun 08:00-17:00", ...].
const resumenHorario = (corte: any): string[] => {
  const lineas: string[] = [];
  for (const d of DIAS) {
    const ent = corte?.[d.ent];
    const sal = corte?.[d.sal];
    if (ent && sal) {
      lineas.push(`${d.label} ${String(ent).slice(0, 5)}-${String(sal).slice(0, 5)}`);
    }
  }
  return lineas;
};

const esSupervisor = (c: any) => c?.supervisor === 1 || c?.supervisor === true || c?.supervisor === "1";

const hoyISO = () => {
  const d = new Date();
  const off = d.getTimezoneOffset();
  return new Date(d.getTime() - off * 60000).toISOString().slice(0, 10);
};

const sumarDias = (fechaISO: string, dias: number) => {
  const d = new Date(`${fechaISO}T00:00:00`);
  d.setDate(d.getDate() + dias);
  const off = d.getTimezoneOffset();
  return new Date(d.getTime() - off * 60000).toISOString().slice(0, 10);
};

// Campos {lun_ent, lun_sal, ...} solo para los dias con entrada y salida.
const horarioDeCorte = (corte: any) => {
  const campos: Record<string, string> = {};
  for (const d of DIAS) {
    const ent = corte?.[d.ent];
    const sal = corte?.[d.sal];
    if (ent && sal) {
      campos[d.ent] = String(ent).slice(0, 5);
      campos[d.sal] = String(sal).slice(0, 5);
    }
  }
  return campos;
};

const ModalMejorOperario: React.FC<Props> = ({ open, psId, corte, operario, onClose, onReemplazado }) => {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down("xs"));

  const [buscando, setBuscando] = useState(false);
  const [candidatos, setCandidatos] = useState<any[]>([]);
  const [seleccionado, setSeleccionado] = useState<any | null>(null);
  const [fechaCierre, setFechaCierre] = useState(hoyISO());
  const [procesando, setProcesando] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [alertMsg, setAlertMsg] = useState("");

  const legajoActual = String(operario?.legajo || "");

  useEffect(() => {
    if (!open || !corte) return;
    setCandidatos([]);
    setSeleccionado(null);
    setFechaCierre(hoyISO());
    (async () => {
      setBuscando(true);
      const filtros: any = {
        ps_id: psId,
        // El analisis usa la fecha de hoy como referencia (mismo flujo que EMSYS).
        fecha_inicio: hoyISO(),
        perfil: corte.perfil_id ?? "",
        genero: "I",
        total_horas: corte.total_horas,
        ...horarioDeCorte(corte),
      };
      const res = await AsignacionesV2Service.buscarCandidatos(filtros);
      // Excluimos al operario actual de la lista de candidatos seleccionables.
      const lista = (res || []).filter(
        (c: any) => String(c.nroLegajo || c.legajo || "") !== legajoActual
      );
      setCandidatos(lista);
      setBuscando(false);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, corte, psId]);

  const seleccionarCandidato = (cand: any) => setSeleccionado(cand);

  const pedirConfirmacion = () => {
    if (!seleccionado) {
      setAlertMsg("Seleccioná un operario de la lista.");
      return;
    }
    if (!fechaCierre) {
      setAlertMsg("Indicá la fecha de cierre de la asignación actual.");
      return;
    }
    setConfirmOpen(true);
  };

  const confirmarReemplazo = async () => {
    setConfirmOpen(false);
    if (!seleccionado || !corte) return;

    const legajo = seleccionado.nroLegajo || seleccionado.legajo || "";
    const nombre = seleccionado.nombres || seleccionado.nombre || seleccionado.operario || "";
    const horario = horarioDeCorte(corte);
    const fechaInicioNueva = sumarDias(fechaCierre, 1);

    setProcesando(true);
    try {
      // 1) Cerramos la asignación actual con la fecha de corte elegida.
      await AsignacionesV2Service.cerrar(corte.id, fechaCierre);
      // 2) Creamos la nueva asignación con el operario elegido y el mismo horario.
      const datos: any = {
        operario: `${nombre} - ${legajo}`,
        fecha_inicio: fechaInicioNueva,
        ps_id: psId,
        perfil_id: corte.perfil_id ?? "",
        supervisor: esSupervisor(corte),
        cortes: [horario],
        ...horario,
      };
      const resp = await AsignacionesV2Service.guardar(datos);
      if (resp?.status === 200) {
        setAlertMsg(resp?.data?.mensaje || "Reemplazo realizado correctamente.");
        onReemplazado();
      } else {
        setAlertMsg(
          resp?.data?.error ||
            "La asignación actual se cerró, pero no se pudo crear la nueva. Verificá e intentá crearla manualmente."
        );
      }
    } catch (error: any) {
      setAlertMsg(error?.response?.data?.error || "Ocurrió un error al realizar el reemplazo.");
    } finally {
      setProcesando(false);
    }
  };

  const nombreSeleccionado = seleccionado
    ? `${seleccionado.nombres || seleccionado.nombre || seleccionado.operario || ""} - ${
        seleccionado.nroLegajo || seleccionado.legajo || ""
      }`
    : "";

  return (
    <Dialog open={open} onClose={onClose} scroll="paper" maxWidth="md" fullWidth fullScreen={fullScreen}>
      <IonAlert isOpen={!!alertMsg} onDidDismiss={() => setAlertMsg("")} message={alertMsg} buttons={["Cerrar"]} />
      <DialogTitle disableTypography style={{ background: "#910434" }}>
        <Typography variant="h6" style={{ color: "#ffffff", fontWeight: "bold" }}>
          Buscar mejor operario
        </Typography>
      </DialogTitle>
      <DialogContent dividers style={{ overflowX: "hidden" }}>
        <Box style={{ background: "#eef2f7", borderRadius: 4, padding: 10, marginBottom: 12 }}>
          <Typography variant="body2" style={{ fontWeight: "bold" }}>
            ¿Cómo funciona?
          </Typography>
          <Typography variant="caption" component="div">
            1) Revisá los candidatos sugeridos por la IA (ordenados de mejor a peor). · 2) Elegí uno. ·
            3) Se cierra la asignación actual y se crea una nueva con el mismo horario.
          </Typography>
          <Typography variant="caption" component="div" style={{ color: "#666", marginTop: 4 }}>
            El análisis usa la fecha de hoy como referencia.
          </Typography>
        </Box>

        <Typography variant="body2" style={{ marginBottom: 8 }}>
          Operario actual: <strong>{operario?.operario || "-"}</strong>
          {legajoActual ? ` (Legajo ${legajoActual})` : ""}
        </Typography>

        {buscando ? (
          <Typography variant="body2" style={{ padding: 16, textAlign: "center", color: "#666" }}>
            Analizando la asignación y buscando candidatos óptimos...
          </Typography>
        ) : candidatos.length === 0 ? (
          <Typography variant="body2" style={{ padding: 16, textAlign: "center", color: "#666" }}>
            No se encontraron candidatos disponibles para este horario.
          </Typography>
        ) : (
          <>
            <Typography variant="caption" style={{ display: "block", marginBottom: 4, color: "#666" }}>
              Candidatos ordenados por recomendación de la IA (el #1 es el mejor).
            </Typography>
            <List dense>
              {candidatos.map((cand, idx) => {
                const legajo = cand.nroLegajo || cand.legajo || "";
                const nombre = cand.nombres || cand.nombre || cand.operario || "";
                const etiqueta = `${nombre} - ${legajo}`;
                const sel = nombreSeleccionado === etiqueta;
                const dist = cand.distancia_km_dinamica;
                const detalle = [
                  cand.perfil ? `Perfil: ${cand.perfil}` : null,
                  dist != null ? `Dist: ${Number(dist).toFixed(1)} km` : null,
                  cand.horas_total_asignadas != null ? `Horas: ${cand.horas_total_asignadas}` : null,
                  cand.tiene_movilidad ? "Con movilidad" : null,
                  cand.justificacion ? `— ${cand.justificacion}` : null,
                ]
                  .filter(Boolean)
                  .join("  •  ");
                return (
                  <ListItem
                    key={`${legajo}-${idx}`}
                    button
                    selected={sel}
                    onClick={() => seleccionarCandidato(cand)}
                    style={sel ? { border: "2px solid #910434", borderRadius: 4 } : {}}
                  >
                    <ListItemText
                      primary={
                        <span>
                          {etiqueta}
                          {idx === 0 && (
                            <Chip
                              size="small"
                              label="Recomendado"
                              style={{ marginLeft: 8, background: "#2e7d32", color: "white" }}
                            />
                          )}
                          {cand.score_ia != null ? `   ·   IA: ${cand.score_ia}` : ""}
                        </span>
                      }
                      secondary={detalle}
                    />
                  </ListItem>
                );
              })}
            </List>

            <Box style={{ marginTop: 8 }}>
              <TextField
                type="date"
                label="Fecha de cierre de la asignación actual"
                size="small"
                value={fechaCierre}
                onChange={(e) => setFechaCierre(e.target.value)}
                InputLabelProps={{ shrink: true }}
                fullWidth
              />
              <Typography variant="caption" style={{ color: "#666" }}>
                La nueva asignación comenzará el {sumarDias(fechaCierre, 1)}.
              </Typography>
            </Box>
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={procesando}>
          Cancelar
        </Button>
        <Button
          onClick={pedirConfirmacion}
          variant="contained"
          disabled={!seleccionado || procesando}
          style={{ background: seleccionado && !procesando ? "#910434" : "#cccccc", color: "white" }}
        >
          {procesando ? "Procesando..." : "Reemplazar operario"}
        </Button>
      </DialogActions>

      {/* Confirmacion del reemplazo */}
      <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)}>
        <DialogTitle disableTypography style={{ background: "#910434" }}>
          <Typography variant="h6" style={{ color: "#ffffff", fontWeight: "bold" }}>
            Confirmar reemplazo
          </Typography>
        </DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" style={{ marginBottom: 8 }}>
            Se cerrará la asignación de <strong>{operario?.operario || "el operario actual"}</strong> el{" "}
            <strong>{fechaCierre}</strong> y se creará una nueva, ya cargada con los mismos horarios, para{" "}
            <strong>{nombreSeleccionado}</strong> a partir del <strong>{sumarDias(fechaCierre, 1)}</strong>.
          </Typography>
          <Box style={{ background: "#f5f5f5", borderRadius: 4, padding: 8, marginBottom: 4 }}>
            <Typography variant="caption" style={{ fontWeight: "bold", display: "block", marginBottom: 2 }}>
              Horario que se asignará al nuevo operario:
            </Typography>
            {corte && resumenHorario(corte).length > 0 ? (
              resumenHorario(corte).map((linea) => (
                <Typography key={linea} variant="caption" component="div">
                  • {linea}
                </Typography>
              ))
            ) : (
              <Typography variant="caption">Sin horario cargado.</Typography>
            )}
          </Box>
          <Typography variant="body2" style={{ marginTop: 8 }}>
            ¿Confirmás el reemplazo?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmOpen(false)}>Cancelar</Button>
          <Button variant="contained" style={{ background: "#910434", color: "white" }} onClick={confirmarReemplazo}>
            Confirmar
          </Button>
        </DialogActions>
      </Dialog>
    </Dialog>
  );
};

export default ModalMejorOperario;
